const apiBaseUrl = (import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8000/api/v1').replace(/\/$/, '');
const sessionStorageKey = 'brand-field-crew.session';

export interface TokenPair {
  access: string;
  refresh: string;
}

let tokenRefreshPromise: Promise<TokenPair> | null = null;

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly details: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export function getApiErrorMessage(error: unknown): string {
  if (!(error instanceof ApiError)) {
    return error instanceof Error ? error.message : 'Something went wrong. Please try again.';
  }
  if (!error.details || typeof error.details !== 'object') return error.message;

  const details = error.details as Record<string, unknown>;
  const collectMessages = (value: unknown): string[] => {
    if (typeof value === 'string') return [value];
    if (Array.isArray(value)) return value.flatMap(collectMessages);
    if (value && typeof value === 'object') return Object.values(value).flatMap(collectMessages);
    return [];
  };
  const messages = collectMessages(details.errors ?? details);
  return messages[0] ?? error.message;
}

async function parseResponse(response: Response): Promise<unknown> {
  const contentType = response.headers.get('content-type');
  if (response.status === 204) return null;
  if (contentType?.includes('application/json')) return response.json() as Promise<unknown>;
  return response.text();
}

function readStoredSession(): TokenPair | null {
  const raw = localStorage.getItem(sessionStorageKey);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<TokenPair>;
    return parsed.access && parsed.refresh ? { access: parsed.access, refresh: parsed.refresh } : null;
  } catch {
    return null;
  }
}

async function performRequest<T>(
  path: string,
  options: RequestInit & { accessToken?: string },
  accessToken?: string,
): Promise<T> {
  const headers = new Headers(options.headers);
  headers.set('Accept', 'application/json');
  if (options.body && !(options.body instanceof FormData)) headers.set('Content-Type', 'application/json');
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);

  const response = await fetch(`${apiBaseUrl}${path}`, { ...options, headers });
  const body = await parseResponse(response);

  if (!response.ok) {
    const message = typeof body === 'object' && body && 'detail' in body && typeof body.detail === 'string'
      ? body.detail
      : `Request failed with status ${response.status}`;
    throw new ApiError(message, response.status, body);
  }

  return body as T;
}

async function performDownload(
  path: string,
  options: RequestInit & { accessToken?: string },
  accessToken?: string,
) {
  const headers = new Headers(options.headers);
  // Exports may be CSV or XLSX. Let the server select its declared file response rather than
  // rejecting an XLSX endpoint during DRF content negotiation.
  headers.set('Accept', '*/*');
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);

  const response = await fetch(`${apiBaseUrl}${path}`, { ...options, headers });
  if (!response.ok) {
    const body = await parseResponse(response);
    const message = typeof body === 'object' && body && 'detail' in body && typeof body.detail === 'string'
      ? body.detail
      : `Request failed with status ${response.status}`;
    throw new ApiError(message, response.status, body);
  }
  const disposition = response.headers.get('content-disposition') ?? '';
  const contentType = response.headers.get('content-type')?.toLowerCase() ?? '';
  const fallbackExtension = contentType.includes(
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ) ? 'xlsx' : 'csv';
  const filename = disposition.match(/filename="?([^";]+)"?/i)?.[1]
    ?? `brand-field-crew-export.${fallbackExtension}`;
  return { blob: await response.blob(), filename };
}

async function renewSession(refresh: string): Promise<TokenPair> {
  if (!tokenRefreshPromise) {
    tokenRefreshPromise = performRequest<{ access: string; refresh?: string }>(
      '/auth/token/refresh/',
      {
        method: 'POST',
        body: JSON.stringify({ refresh }),
      },
    ).then((tokens) => {
      const session = { access: tokens.access, refresh: tokens.refresh ?? refresh };
      localStorage.setItem(sessionStorageKey, JSON.stringify(session));
      return session;
    }).catch((error: unknown) => {
      if (readStoredSession()?.refresh === refresh) localStorage.removeItem(sessionStorageKey);
      throw error;
    });
  }

  const pendingRefresh = tokenRefreshPromise;
  try {
    return await pendingRefresh;
  } finally {
    if (tokenRefreshPromise === pendingRefresh) tokenRefreshPromise = null;
  }
}

export async function apiRequest<T>(
  path: string,
  options: RequestInit & { accessToken?: string } = {},
): Promise<T> {
  if (!options.accessToken) return performRequest<T>(path, options);

  const accessToken = readStoredSession()?.access ?? options.accessToken;
  try {
    return await performRequest<T>(path, options, accessToken);
  } catch (error) {
    if (!(error instanceof ApiError) || error.status !== 401) throw error;

    const latestSession = readStoredSession();
    if (!latestSession) throw error;

    if (latestSession.access !== accessToken) {
      return performRequest<T>(path, options, latestSession.access);
    }

    const refreshedSession = await renewSession(latestSession.refresh);
    return performRequest<T>(path, options, refreshedSession.access);
  }
}

export async function downloadApiFile(
  path: string,
  options: RequestInit & { accessToken?: string } = {},
) {
  const saveFile = ({ blob, filename }: { blob: Blob; filename: string }) => {
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = filename;
    anchor.style.display = 'none';
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(objectUrl);
    return filename;
  };

  if (!options.accessToken) return saveFile(await performDownload(path, options));

  const accessToken = readStoredSession()?.access ?? options.accessToken;
  try {
    return saveFile(await performDownload(path, options, accessToken));
  } catch (error) {
    if (!(error instanceof ApiError) || error.status !== 401) throw error;

    const latestSession = readStoredSession();
    if (!latestSession) throw error;
    if (latestSession.access !== accessToken) {
      return saveFile(await performDownload(path, options, latestSession.access));
    }
    const refreshedSession = await renewSession(latestSession.refresh);
    return saveFile(await performDownload(path, options, refreshedSession.access));
  }
}

export function requestToken(email: string, password: string) {
  return apiRequest<TokenPair>('/auth/token/', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
}

export function refreshAccessToken(refresh: string) {
  return apiRequest<{ access: string; refresh?: string }>('/auth/token/refresh/', {
    method: 'POST',
    body: JSON.stringify({ refresh }),
  });
}
